<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { mentorshipApi } from '../services/mentorshipApi'
import { MENTORSHIP_STATUS_COLORS, MENTORSHIP_STATUS_LABELS } from '../constants/statuses'
import type { CaseDocument, Competency, CompetencyAssessment, DiaryEntry, FeedbackResponse, IndividualProgram, KnowledgeMaterial, KnowledgeMaterialVersion, KnowledgeTest, MaterialAcknowledgement, MentorshipCapabilities, MentorshipCaseListItem, MentorshipDocumentCatalogItem, MentorshipDocumentTemplate, MentorshipHistoryEntry, MentorshipMeeting, MentorshipTaskLink, ProgramTemplate, RiskSignal, TestAttempt } from '../types'
import MentorshipDialog from './MentorshipDialog.vue'
import MentorshipLinkPicker from './MentorshipLinkPicker.vue'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import { formatDateTime } from '@/utils/formatters'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeDepartment, IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import { ERouteNames } from '@/router/ERouteNames'
import { getApiError } from '@/composables/getApiError'
import { useUserStore } from '@/modules/user/stores/userStore'
import { useTasksStore } from '@/modules/tasks/stores/tasksStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { handleNotificationUrl } from '@/modules/notifications/utils/notificationNavigation'

const caseDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const transitionDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const protocolDialogRef = ref<InstanceType<typeof MentorshipDialog>>()
const route = useRoute()
const router = useRouter()
const apiStore = useApiStore()
const userStore = useUserStore()
const tasksStore = useTasksStore()
const feedbackStore = useFeedbackStore()
const openingTask = ref<string | null>(null)
const isCreate = computed(() => route.name === ERouteNames.MENTORSHIP_CASE_CREATE)
const loading = ref(false)
const editing = ref(false)
const formRef = ref()
const capabilities = ref<MentorshipCapabilities | null>(null)
const acknowledgements = ref<MaterialAcknowledgement[]>([])
const success = ref('')
const transitionTarget = ref('')
const transitionReason = ref('')
const transitionLabels: Record<string, string> = { draft: 'Вернуть в черновик', pending_approval: 'На согласование', active: 'Начать / возобновить', paused: 'Приостановить', final_review: 'На итоговую проверку', completed: 'Завершить', cancelled: 'Отменить', archived: 'В архив' }
const dateLabel = (value: string) => value ? value.split('-').reverse().join('.') : '—'
const required = (value: unknown) => !!String(value || '').trim() || 'Обязательное поле'
const documentDownload = ref<{ url: string
  filename: string } | null>(null)
const protocolEdit = ref<MentorshipMeeting | null>(null)
const protocolText = ref('')
const protocolReason = ref('')
const unlinkTarget = ref<{ id: string
  kind: 'task' | 'meeting' } | null>(null)
const saving = ref(false)
const error = ref('')
const loadError = ref('')
const caseItem = ref<MentorshipCaseListItem | null>(null)
const permissions = computed(() => caseItem.value?.permissions)
const program = ref<IndividualProgram | null>(null)
const templates = ref<ProgramTemplate[]>([])
const diaryEntries = ref<DiaryEntry[]>([])
const competencies = ref<Competency[]>([])
const assessments = ref<CompetencyAssessment[]>([])
const materials = ref<KnowledgeMaterial[]>([])
const documents = ref<CaseDocument[]>([])
const generatedDocuments = ref<MentorshipDocumentCatalogItem[]>([])
const feedback = ref<FeedbackResponse[]>([])
const risks = ref<RiskSignal[]>([])
const knowledgeTests = ref<KnowledgeTest[]>([])
const testAttempt = ref<TestAttempt | null>(null)
const testAttempts = ref<TestAttempt[]>([])
const uploadFile = ref<File | null>(null)
const uploadTemplate = ref<string | null>(null)
const documentTemplates = ref<MentorshipDocumentTemplate[]>([])
const requiredDocumentTemplates = computed(() => capabilities.value?.require_final_documents === false ? [] : documentTemplates.value.filter(item => item.is_active && item.is_required))
const missingDocuments = computed(() => requiredDocumentTemplates.value.filter(item => !documents.value.some(document => document.template === item.id && document.confirmed_at)))
const primaryAction = computed(() => {
  const item = caseItem.value
  if (!item) return undefined
  if (item.status === 'archived') return item.available_actions[0]
  const next: Record<string, string> = { draft: 'pending_approval', pending_approval: 'active', active: 'final_review', paused: 'active', final_review: 'completed', completed: 'archived', cancelled: 'archived' }
  return item.available_actions.find(action => action === next[item.status])
})
const secondaryActions = computed(() => caseItem.value?.available_actions.filter(action => action !== primaryAction.value) || [])
function actionLabel(action: string) {
  if (caseItem.value?.status === 'archived') return 'Восстановить из архива'
  if (action === 'active') {
    if (caseItem.value?.status === 'paused') return 'Возобновить'
    if (caseItem.value?.status === 'final_review') return 'Вернуть в работу'
    return 'Начать наставничество'
  }
  return transitionLabels[action]
}
const selectedTestVersion = ref('')
const testAnswers = ref<Record<string, unknown>>({})
const taskLinks = ref<MentorshipTaskLink[]>([])
const meetings = ref<MentorshipMeeting[]>([])
const taskLinkForm = ref({ task: null as number | string | null, activity: null as string | null })
const meetingForm = ref({ calendar_event: null as number | string | null, activity: null as string | null, protocol: '' })
const tab = ref('program')
const historyVisible = ref(false)
const history = ref<MentorshipHistoryEntry[]>([])
const historyLoading = ref(false)
const historyError = ref('')
const historyPage = ref(1)
const historyCount = ref(0)
let historySequence = 0
async function loadHistory() {
  if (!caseItem.value) return
  const sequence = ++historySequence
  historyLoading.value = true
  historyError.value = ''
  try {
    const result = await mentorshipApi.getHistory(caseItem.value.id, historyPage.value)
    if (sequence !== historySequence) return
    history.value = result.results
    historyCount.value = result.count
  } catch (e) {
    if (sequence === historySequence) historyError.value = getApiError(e, 'Не удалось загрузить историю')
  } finally {
    if (sequence === historySequence) historyLoading.value = false
  }
}
function toggleHistory() {
  historyVisible.value = !historyVisible.value
  tab.value = historyVisible.value ? 'history' : 'program'
  if (historyVisible.value) loadHistory()
}
watch(historyPage, loadHistory)
async function cancelEdit() {
  if (await caseDialogRef.value?.confirmDismiss()) editing.value = false
}
async function confirmLeave() {
  if (transitionTarget.value && !(await transitionDialogRef.value?.confirmDismiss())) return false
  if (protocolEdit.value && !(await protocolDialogRef.value?.confirmDismiss())) return false
  return caseDialogRef.value?.confirmDismiss() ?? true
}
onBeforeRouteLeave(confirmLeave)
onBeforeRouteUpdate((to, from) => to.name !== from.name || to.params.id !== from.params.id ? confirmLeave() : true)
const diaryText = ref('')
const diaryActivity = ref<string | null>(null)
const assessmentForm = ref({ competency: '', assessment_type: 'intermediate', level: null as number | null, comment: '' })
const feedbackForm = ref({ score: 5, text: '', is_confidential: false })
const menteeModal = ref<InstanceType<typeof EmployeeSelectionModal>>()
const mentorModal = ref<InstanceType<typeof EmployeeSelectionModal>>()
const menteeName = ref('')
const mentorName = ref('')
const form = ref({ mentee: '', mentor: '', department: '', template_version: '', basis: '', start_date: '', end_date: '', assignment_kind: 'local' as 'local' | 'statutory', statutory_basis: '', payment_terms: '' })
const departmentOptions = computed(() => {
  const result: { title: string
    value: string }[] = []
  function walk(items: IEmployeeTreeDepartment[], parents: string[] = []) {
    for (const item of items) {
      const title = [...parents, item.name].join(' / ')
      result.push({ title, value: item.id })
      walk(item.children || [], [...parents, item.name])
    }
  }
  walk(apiStore.employees)
  if (caseItem.value && !result.some(item => item.value === caseItem.value?.department)) result.push({ title: caseItem.value.department_name, value: caseItem.value.department })
  return result
})
const currentRole = computed(() => {
  if (!caseItem.value || !userStore.user) return 'Наблюдатель'
  if (caseItem.value.mentor === userStore.user.id) return 'Наставник'
  if (caseItem.value.mentee === userStore.user.id) return 'Наставляемый'
  return capabilities.value?.can_manage ? 'Координатор' : 'Наблюдатель'
})
const nextStepText = computed(() => {
  if (!caseItem.value) return ''
  if (caseItem.value.status === 'draft') return caseItem.value.available_actions.includes('pending_approval') ? 'Проверьте участников и программу, затем отправьте на согласование.' : 'Назначение в черновике. Координатор проверит участников и программу перед согласованием.'
  if (caseItem.value.status === 'active') return currentRole.value === 'Наставляемый' ? 'Откройте программу и выполните ближайшее задание.' : 'Проверьте ход программы и новые записи дневника.'
  if (caseItem.value.status === 'final_review') return permissions.value?.can_assess ? 'Проведите итоговую оценку и сформируйте заключение.' : 'Наставник проверяет результаты программы и готовит итоговое заключение.'
  if (caseItem.value.status === 'completed') return 'Наставничество завершено. Результаты и документы доступны в карточке.'
  if (caseItem.value.status === 'pending_approval') return 'Назначение ожидает согласования. После проверки координатор начнёт наставничество.'
  if (caseItem.value.status === 'paused') return 'Наставничество приостановлено. Координатор сможет возобновить его.'
  return 'Откройте программу и проверьте ход наставничества.'
})

const publishedVersions = computed(() => templates.value.flatMap(template => template.versions
  .filter(version => version.status === 'published' || version.id === caseItem.value?.template_version)
  .map(version => ({ title: `${template.name} · версия ${version.version}`, value: version.id }))))
const activityOptions = computed(() => program.value?.stages.flatMap(stage => stage.activities.map(activity => ({ title: `${stage.name}: ${activity.name}`, value: activity.id }))) || [])
const activityStatusLabels: Record<string, string> = { planned: 'Запланировано', in_progress: 'В работе', submitted: 'На проверке', revision_required: 'На доработке', completed: 'Выполнено', cancelled: 'Отменено' }
const assessmentTypeLabels: Record<string, string> = { initial: 'Стартовая', intermediate: 'Промежуточная', final: 'Итоговая' }
const selectedCompetencyLevels = computed(() => competencies.value.find(item => item.id === assessmentForm.value.competency)?.levels.map(level => ({ title: `${level.value} — ${level.name}`, value: level.value })) || [])
const publishedTestVersions = computed(() => knowledgeTests.value.flatMap(test => test.versions.filter(version => version.published_at).map(version => ({ title: `${test.title} · версия ${version.version} · ${version.time_limit_minutes} мин.`, value: version.id }))))
const documentStageLabels: Record<string, { title: string
  description: string
  icon: string }> = {
    start: { title: 'Стартовый комплект', description: 'Оформляется после назначения участников и программы.', icon: 'ri-flag-line' },
    middle: { title: 'В ходе наставничества', description: 'Краткий срез прогресса без повторного заполнения данных.', icon: 'ri-line-chart-line' },
    finish: { title: 'Итоговый комплект', description: 'Появляется на итоговой проверке и при завершении.', icon: 'ri-checkbox-circle-line' },
  }

function findDepartment(departments: IEmployeeTreeDepartment[], employeeId: string): string {
  for (const department of departments) {
    if (department.employees.some(employee => employee.id === employeeId)) return department.id
    const nested = findDepartment(department.children || [], employeeId)
    if (nested) return nested
  }
  return ''
}

function selectMentee(employee: IEmployeeTreeItem) {
  form.value.mentee = employee.id
  form.value.department = findDepartment(apiStore.employees, employee.id)
  menteeName.value = employee.full_name
}

function selectMentor(employee: IEmployeeTreeItem) {
  form.value.mentor = employee.id
  mentorName.value = employee.full_name
}

function startEdit() {
  if (!caseItem.value) return
  const item = caseItem.value
  form.value = { mentee: item.mentee, mentor: item.mentor || '', department: item.department, template_version: item.template_version || '', basis: item.basis, start_date: item.start_date, end_date: item.end_date, assignment_kind: item.assignment_kind || 'local', statutory_basis: item.statutory_basis || '', payment_terms: item.payment_terms || '' }
  menteeName.value = item.mentee_name
  mentorName.value = item.mentor_name || ''
  editing.value = true
  error.value = ''
}
async function save() {
  error.value = ''
  if (!(await formRef.value?.validate())?.valid) return
  if (!form.value.mentee || !form.value.department || !form.value.basis || !form.value.start_date || !form.value.end_date) {
    error.value = 'Заполните обязательные поля.'
    return
  }
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const payload = { ...form.value, basis: form.value.basis.trim(), mentor: form.value.mentor || null, template_version: form.value.template_version || null }
    if (editing.value && caseItem.value) {
      caseItem.value = await mentorshipApi.updateCase(caseItem.value.id, payload)
      editing.value = false
      success.value = 'Изменения сохранены'
      await load()
    } else {
      const created = await mentorshipApi.createCase(payload)
      caseDialogRef.value?.markSaved()
      saving.value = false
      await router.replace({ name: ERouteNames.MENTORSHIP_CASE_DETAIL, params: { id: created.id }, query: route.query })
    }
  } catch (e) {
    error.value = getApiError(e, 'Не удалось сохранить наставничество')
  } finally {
    saving.value = false
  }
}

function openTransition(targetStatus: string) {
  error.value = ''
  success.value = ''
  transitionReason.value = ''
  transitionTarget.value = targetStatus
}

async function transition(targetStatus: string) {
  if (!caseItem.value) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    error.value = ''
    caseItem.value = await mentorshipApi.transitionCase(caseItem.value.id, targetStatus, transitionReason.value.trim())
    transitionTarget.value = ''
    transitionReason.value = ''
    await load()
  } catch (e) {
    error.value = getApiError(e, 'Не удалось изменить статус дела')
  } finally {
    saving.value = false
  }
}

async function addDiaryEntry() {
  if (!caseItem.value || !diaryText.value.trim()) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await mentorshipApi.createDiaryEntry({ case: caseItem.value.id, activity: diaryActivity.value, entry_date: new Date().toLocaleDateString('en-CA'), text: diaryText.value.trim() })
    diaryText.value = ''
    diaryActivity.value = null
    diaryEntries.value = await mentorshipApi.getDiaryEntries(caseItem.value.id)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось добавить запись дневника')
  } finally {
    saving.value = false
  }
}

async function confirmDiary(entry: DiaryEntry) {
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await mentorshipApi.confirmDiaryEntry(entry.id)
    if (caseItem.value) diaryEntries.value = await mentorshipApi.getDiaryEntries(caseItem.value.id)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось подтвердить запись')
  } finally {
    saving.value = false
  }
}

async function transitionProgramActivity(activityId: string, targetStatus: string) {
  if (!caseItem.value) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await mentorshipApi.transitionActivity(activityId, targetStatus)
    ;[caseItem.value, program.value] = await Promise.all([mentorshipApi.getCase(caseItem.value.id), mentorshipApi.getCaseProgram(caseItem.value.id)])
  } catch (e) {
    error.value = getApiError(e, 'Не удалось изменить состояние мероприятия')
  } finally {
    saving.value = false
  }
}

async function addAssessment() {
  if (!caseItem.value || !assessmentForm.value.competency || assessmentForm.value.level === null) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await mentorshipApi.createCompetencyAssessment({ case: caseItem.value.id, ...assessmentForm.value, level: assessmentForm.value.level })
    assessments.value = await mentorshipApi.getCompetencyAssessments(caseItem.value.id)
    assessmentForm.value = { competency: '', assessment_type: 'intermediate', level: null, comment: '' }
  } catch (e) {
    error.value = getApiError(e, 'Не удалось сохранить оценку компетенции')
  } finally {
    saving.value = false
  }
}

async function downloadMaterial(version: KnowledgeMaterialVersion) {
  saving.value = true
  error.value = ''
  try {
    if (documentDownload.value) URL.revokeObjectURL(documentDownload.value.url)
    documentDownload.value = null
    documentDownload.value = await mentorshipApi.downloadMaterialVersion(version.id, version.file_name || 'Материал наставничества')
  } catch (e) {
    error.value = getApiError(e, 'Не удалось скачать материал')
  } finally {
    saving.value = false
  }
}
async function acknowledgeMaterial(versionId: string) {
  if (!caseItem.value) return
  saving.value = true
  error.value = ''
  try {
    await mentorshipApi.acknowledgeMaterial({ case: caseItem.value.id, version: versionId })
    acknowledgements.value = await mentorshipApi.getAcknowledgements(caseItem.value.id)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось подтвердить ознакомление')
  } finally {
    saving.value = false
  }
}

async function addFeedback() {
  if (!caseItem.value) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await mentorshipApi.createFeedback({ case: caseItem.value.id, ...feedbackForm.value })
    ;[feedback.value, risks.value] = await Promise.all([mentorshipApi.getFeedback(caseItem.value.id), mentorshipApi.getRiskSignals(caseItem.value.id)])
    feedbackForm.value = { score: 5, text: '', is_confidential: false }
  } catch (e) {
    error.value = getApiError(e, 'Не удалось отправить обратную связь')
  } finally {
    saving.value = false
  }
}

async function startTest() {
  if (!caseItem.value || !selectedTestVersion.value) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    testAttempt.value = await mentorshipApi.createTestAttempt({ case: caseItem.value.id, version: selectedTestVersion.value, activity: null })
    testAnswers.value = {}
  } catch (e) {
    error.value = getApiError(e, 'Не удалось начать тест')
  } finally {
    saving.value = false
  }
}

async function submitTest() {
  if (!testAttempt.value) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const current = await mentorshipApi.getTestAttempt(testAttempt.value.id)
    await Promise.all(Object.entries(testAnswers.value).map(([question, answer]) => mentorshipApi.saveTestAnswer({ attempt: current.id, question, answer }, current.answers.find(item => item.question === question)?.id)))
    testAttempt.value = await mentorshipApi.submitTestAttempt(testAttempt.value.id)
    testAttempts.value = await mentorshipApi.getTestAttempts(caseItem.value!.id)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось отправить тест')
    try {
      testAttempt.value = await mentorshipApi.getTestAttempt(testAttempt.value!.id)
      if (testAttempt.value.status === 'expired') error.value = 'Время прохождения теста истекло. Можно начать новую попытку.'
    } catch { /* Preserve the original submit error if refreshing also fails. */ }
  } finally {
    saving.value = false
  }
}

async function openTask(link: MentorshipTaskLink) {
  openingTask.value = link.id
  error.value = ''
  try {
    const entry = await tasksStore.fetchEntryById(Number(link.task))
    if (!entry || !handleNotificationUrl({ urlProps: { entryId: entry.id, ...entry.url_props }, messageType: 'responsibility_entry' })) {
      error.value = 'Не удалось открыть связанную задачу.'
    }
  } catch (e) {
    error.value = getApiError(e, 'Не удалось открыть связанную задачу')
  } finally {
    openingTask.value = null
  }
}
watch(() => feedbackStore.globalResponsibilityEntryId, async (current, previous) => {
  if (current !== null || previous === null || !caseItem.value || !taskLinks.value.some(link => Number(link.task) === previous)) return
  try {
    taskLinks.value = await mentorshipApi.getTaskLinks(caseItem.value.id)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось обновить связанные задачи')
  }
})

async function linkTask() {
  if (!caseItem.value || taskLinkForm.value.task === null) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await mentorshipApi.createTaskLink({ case: caseItem.value.id, task: taskLinkForm.value.task, activity: taskLinkForm.value.activity })
    taskLinks.value = await mentorshipApi.getTaskLinks(caseItem.value.id)
    taskLinkForm.value = { task: null, activity: null }
  } catch (e) {
    error.value = getApiError(e, 'Не удалось связать задачу')
  } finally {
    saving.value = false
  }
}

async function linkMeeting() {
  if (!caseItem.value || meetingForm.value.calendar_event === null) return
  saving.value = true
  error.value = ''
  success.value = ''
  try {
    await mentorshipApi.createMeeting({
      case: caseItem.value.id,
      calendar_event: meetingForm.value.calendar_event,
      activity: meetingForm.value.activity,
      protocol: meetingForm.value.protocol,
    })
    meetings.value = await mentorshipApi.getMeetings(caseItem.value.id)
    meetingForm.value = { calendar_event: null, activity: null, protocol: '' }
  } catch (e) {
    error.value = getApiError(e, 'Не удалось связать встречу')
  } finally {
    saving.value = false
  }
}

async function downloadDocument(item: MentorshipDocumentCatalogItem, format: 'pdf' | 'docx') {
  if (!caseItem.value || !item.available) return
  saving.value = true
  error.value = ''
  try {
    if (documentDownload.value) URL.revokeObjectURL(documentDownload.value.url)
    documentDownload.value = null
    documentDownload.value = await mentorshipApi.downloadGeneratedDocument(caseItem.value.id, item.code, format)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось сформировать документ')
  } finally {
    saving.value = false
  }
}

let loadSequence = 0
async function load() {
  const sequence = ++loadSequence
  loading.value = true
  error.value = ''
  loadError.value = ''
  try {
    const loadedCapabilities = await mentorshipApi.getCapabilities()
    if (sequence !== loadSequence) return
    capabilities.value = loadedCapabilities
    if (isCreate.value) {
      caseItem.value = null
      if (!capabilities.value.can_create) {
        error.value = 'Назначение наставничества доступно координатору.'
        return
      }
    } else {
      const loadedCase = await mentorshipApi.getCase(String(route.params.id))
      if (sequence !== loadSequence) return
      caseItem.value = loadedCase
    }
    async function assign<T>(target: { value: T }, request: Promise<T>) {
      const value = await request
      if (sequence === loadSequence) target.value = value
    }
    const requests: Array<{ label: string
      load: () => Promise<void> }> = [
        {
          label: 'Программы',
          load: async () => {
            await assign(templates, mentorshipApi.getProgramTemplates())
          },
        },
        {
          label: 'Сотрудники',
          load: async () => {
            if (!apiStore.employees.length) await apiStore.fetchAllEmployees({ silent: true })
          },
        },
      ]
    if (!isCreate.value && caseItem.value) {
      const id = caseItem.value.id
      requests.push(
        {
          label: 'Программа',
          load: async () => {
            await assign(program, mentorshipApi.getCaseProgram(id))
          },
        },
        {
          label: 'Дневник',
          load: async () => {
            await assign(diaryEntries, mentorshipApi.getDiaryEntries(id))
          },
        },
        {
          label: 'Компетенции',
          load: async () => {
            await assign(competencies, mentorshipApi.getCompetencies())
            await assign(assessments, mentorshipApi.getCompetencyAssessments(id))
          },
        },
        {
          label: 'Материалы',
          load: async () => {
            await assign(materials, mentorshipApi.getKnowledgeMaterials())
            await assign(acknowledgements, mentorshipApi.getAcknowledgements(id))
          },
        },
        {
          label: 'Тесты',
          load: async () => {
            await assign(knowledgeTests, mentorshipApi.getKnowledgeTests())
            await assign(testAttempts, mentorshipApi.getTestAttempts(id))
            const active = testAttempts.value.find(item => item.employee === userStore.user?.id && item.status === 'active')
            if (active && !testAttempt.value) selectAttempt(active)
          },
        },
        {
          label: 'Документы',
          load: async () => {
            await assign(documents, mentorshipApi.getCaseDocuments(id))
            await assign(documentTemplates, mentorshipApi.getDocumentTemplates())
            await assign(generatedDocuments, mentorshipApi.getDocumentCatalog(id))
          },
        },
        {
          label: 'Обратная связь',
          load: async () => {
            await assign(feedback, mentorshipApi.getFeedback(id))
            await assign(risks, mentorshipApi.getRiskSignals(id))
          },
        },
        {
          label: 'Задачи и встречи',
          load: async () => {
            await assign(taskLinks, mentorshipApi.getTaskLinks(id))
            await assign(meetings, mentorshipApi.getMeetings(id))
          },
        },
      )
    }
    const results = await Promise.allSettled(requests.map(request => request.load()))
    if (sequence !== loadSequence) return
    const failures = results.flatMap((result, index) => result.status === 'rejected' ? [requests[index].label] : [])
    if (failures.length) loadError.value = `Не удалось загрузить: ${failures.join(', ')}. Повторите загрузку.`
  } catch (e) {
    if (sequence === loadSequence) loadError.value = getApiError(e, 'Не удалось загрузить карточку наставничества')
  } finally {
    if (sequence === loadSequence) loading.value = false
  }
}
async function confirmDocument(document: CaseDocument) {
  saving.value = true
  error.value = ''
  try {
    await mentorshipApi.confirmCaseDocument(document.id)
    documents.value = await mentorshipApi.getCaseDocuments(caseItem.value!.id)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось подтвердить документ')
  } finally {
    saving.value = false
  }
}
async function saveProtocol() {
  if (!protocolEdit.value) return
  saving.value = true
  error.value = ''
  try {
    await mentorshipApi.updateMeeting(protocolEdit.value.id, { protocol: protocolText.value, reason: protocolReason.value })
    meetings.value = await mentorshipApi.getMeetings(caseItem.value!.id)
    protocolEdit.value = null
  } catch (e) {
    error.value = getApiError(e, 'Не удалось сохранить протокол')
  } finally {
    saving.value = false
  }
}
async function unlink() {
  if (!unlinkTarget.value || !caseItem.value) return
  saving.value = true
  error.value = ''
  try {
    if (unlinkTarget.value.kind === 'task') {
      await mentorshipApi.removeTaskLink(unlinkTarget.value.id)
      taskLinks.value = await mentorshipApi.getTaskLinks(caseItem.value.id)
    } else {
      await mentorshipApi.removeMeeting(unlinkTarget.value.id)
      meetings.value = await mentorshipApi.getMeetings(caseItem.value.id)
    }
    unlinkTarget.value = null
  } catch (e) {
    error.value = getApiError(e, 'Не удалось убрать связь')
  } finally {
    saving.value = false
  }
}

function selectAttempt(attempt: TestAttempt) {
  testAttempt.value = attempt
  testAnswers.value = Object.fromEntries((attempt.answers || []).map(item => [item.question, item.answer]))
}
async function uploadDocument() {
  if (!caseItem.value || !uploadFile.value) return
  if (uploadFile.value.size > 20 * 1024 * 1024) {
    error.value = 'Размер файла не должен превышать 20 МБ.'
    return
  }
  saving.value = true
  error.value = ''
  try {
    await mentorshipApi.uploadDocument(caseItem.value.id, uploadFile.value, uploadTemplate.value)
    uploadFile.value = null
    uploadTemplate.value = null
    documents.value = await mentorshipApi.getCaseDocuments(caseItem.value.id)
    success.value = 'Документ загружен'
  } catch (e) {
    error.value = getApiError(e, 'Не удалось загрузить документ')
  } finally {
    saving.value = false
  }
}
async function downloadUploadedDocument(item: CaseDocument) {
  saving.value = true
  error.value = ''
  try {
    if (documentDownload.value) URL.revokeObjectURL(documentDownload.value.url)
    documentDownload.value = null
    documentDownload.value = await mentorshipApi.downloadDocument(item)
  } catch (e) {
    error.value = getApiError(e, 'Не удалось скачать документ')
  } finally {
    saving.value = false
  }
}
function close() {
  router.push({ name: ERouteNames.MENTORSHIP_CASES, query: route.query })
}
watch(() => [route.name, route.params.id], () => {
  ++historySequence
  historyVisible.value = false
  history.value = []
  historyPage.value = 1
  tab.value = 'program'
  editing.value = false
  caseItem.value = null
  program.value = null
  diaryEntries.value = []
  assessments.value = []
  documents.value = []
  feedback.value = []
  risks.value = []
  taskLinks.value = []
  meetings.value = []
  generatedDocuments.value = []
  testAttempt.value = null
  testAttempts.value = []
  success.value = ''
  load()
}, { immediate: true })
onBeforeUnmount(() => {
  ++loadSequence
  ++historySequence
  if (documentDownload.value) URL.revokeObjectURL(documentDownload.value.url)
})
</script>

<template>
  <MentorshipDialog
    ref="caseDialogRef"
    model-value
    :title="isCreate ? 'Назначить наставничество' : editing ? `Изменить наставничество №${caseItem?.number || ''}` : caseItem ? `Наставничество №${caseItem.number}` : 'Наставничество'"
    :subtitle="isCreate ? 'Укажите сотрудника, наставника и основание назначения.' : caseItem?.mentee_name"
    embedded
    :busy="saving"
    :draft="isCreate || editing ? form : null"
    :actions="caseItem && !editing ? [{ key: 'history', label: historyVisible ? 'Скрыть историю' : 'Показать историю', icon: 'ri-history-line', active: historyVisible }] : []"
    :persistent="!!transitionTarget || !!protocolEdit || !!unlinkTarget"
    @update:model-value="close"
    @action="toggleHistory"
  >
    <template #header-actions>
      <VBtn
        v-if="caseItem && !editing && permissions?.can_edit"
        variant="outlined"
        size="small"
        prepend-icon="ri-edit-line"
        :disabled="saving || loading"
        @click="startEdit"
      >
        Изменить
      </VBtn>
    </template>
    <VTabs
      v-if="caseItem && !editing"
      v-model="tab"
      class="case-tabs px-4"
      show-arrows
    >
      <VTab value="program">
        Программа
      </VTab><VTab value="diary">
        Дневник
      </VTab><VTab value="competencies">
        Компетенции
      </VTab><VTab value="tasks-meetings">
        Задачи и встречи
      </VTab><VTab value="knowledge">
        Материалы
      </VTab><VTab value="tests">
        Тестирование
      </VTab><VTab value="documents">
        Документы
      </VTab><VTab value="feedback">
        Обратная связь
      </VTab>
      <VTab v-if="historyVisible" value="history">
        История
      </VTab>
    </VTabs>
    <VCardText class="entity-form-scroll-body mentorship-case-body">
      <VProgressLinear v-if="loading" indeterminate class="mb-4" />
      <VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        {{ error }}
      </VAlert>
      <VAlert
        v-if="loadError"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        {{ loadError }} <VBtn variant="text" :disabled="loading" @click="load">
          Повторить загрузку
        </VBtn>
      </VAlert>
      <VAlert
        v-if="success"
        type="success"
        variant="tonal"
        class="mb-4"
      >
        {{ success }}
      </VAlert>
      <VForm
        v-if="(isCreate && capabilities?.can_create) || editing"
        ref="formRef"
        :disabled="saving || loading"
        @submit.prevent="save"
      >
        <div class="text-h6 mb-1">
          1. Участники
        </div>
        <div class="text-body-2 text-medium-emphasis mb-4">
          Наставляемый обязателен. Наставника можно назначить позднее.
        </div>
        <VRow>
          <VCol cols="12" md="6">
            <VTextField
              :model-value="menteeName"
              label="Наставляемый *"
              readonly
              :rules="[required]"
              append-inner-icon="ri-user-search-line"
              @click="menteeModal?.open()"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              :model-value="mentorName"
              label="Наставник"
              readonly
              append-inner-icon="ri-user-search-line"
              clearable
              @click:clear="form.mentor = ''; mentorName = ''"
              @click="mentorModal?.open()"
            />
          </VCol>
          <VCol cols="12">
            <VSelect
              v-model="form.department"
              :items="departmentOptions"
              label="Подразделение *"
              :rules="[required]"
            />
          </VCol>
          <VCol cols="12">
            <VDivider class="my-2" />
          </VCol>
          <VCol cols="12">
            <div class="text-h6 mb-1">
              2. Программа
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Готовая программа автоматически создаст этапы, задания и контрольные точки.
            </div>
          </VCol>
          <VCol cols="12">
            <VSelect
              v-model="form.template_version"
              :items="publishedVersions"
              label="Программа наставничества"
              :disabled="editing && !!program"
              :hint="editing && program ? 'Индивидуальный план уже создан по этой версии.' : 'Выберите опубликованную программу для запуска наставничества.'"
              persistent-hint
              clearable
            />
          </VCol>
          <VCol cols="12">
            <VDivider class="my-2" />
          </VCol>
          <VCol cols="12">
            <div class="text-h6 mb-1">
              3. Сроки и основание
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Например: приём нового сотрудника, перевод или освоение новых обязанностей.
            </div>
          </VCol>
          <VCol cols="12">
            <VSelect v-model="form.assignment_kind" :items="[{ value: 'local', title: 'Адаптация / развитие по решению организации' }, { value: 'statutory', title: 'Обязательное наставничество по законодательству' }]" label="Вид наставничества" />
          </VCol>
          <VCol v-if="form.assignment_kind === 'statutory'" cols="12">
            <VAlert type="info" variant="tonal" class="mb-4">
              Ответственный проверяет образование, первую аккредитацию, специальность и место работы, затем указывает срок по приказу №166н. Срок готовой программы адаптации не заменяет установленный законом срок.
            </VAlert>
            <VExpansionPanels variant="accordion" class="mb-4">
              <VExpansionPanel title="Что проверить перед началом">
                <VExpansionPanelText>
                  <ul class="ps-5 mb-3">
                    <li>Наставник соответствует требованиям №167н, включая стаж медицинской деятельности не менее пяти лет, и дал письменное согласие.</li>
                    <li>Руководитель назначил ответственного за организацию наставничества и оформил приказ по сотруднику.</li>
                    <li>Содержание, срок, форма работы и условия оплаты согласованы с наставником в трудовом договоре или дополнительном соглашении.</li>
                  </ul>
                  <a href="https://edu.rosminzdrav.ru/organizacijam/organizacijam/medicinskim-organizacijam/organizacija-nastavnichestva/" target="_blank" rel="noopener noreferrer">Требования и порядок работы на портале Минздрава</a>
                  <p class="mt-2 mb-0">
                    Сведения в НМФО и ФРМР ответственный оформляет отдельно.
                  </p>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
            <VTextarea v-model="form.statutory_basis" label="Применимость требований и расчёт срока" rows="3" />
          </VCol>
          <VCol cols="12">
            <VTextField v-model="form.basis" label="Основание назначения *" :rules="[required]" />
          </VCol>
          <VCol cols="12">
            <VTextarea
              v-model="form.payment_terms"
              label="Размер и условия оплаты наставнику"
              rows="2"
              hint="Для проекта дополнительного соглашения. Укажите конкретные условия, согласованные работодателем и наставником."
              persistent-hint
            />
          </VCol>
          <VCol cols="12" md="6">
            <VInput v-model="form.start_date" :rules="[required]">
              <DateMaskInput
                :model-value="form.start_date"
                :max-date="form.end_date || null"
                :disabled="saving"
                @update:model-value="form.start_date = $event || ''"
              >
                <template #label>
                  Дата начала *
                </template>
              </DateMaskInput>
            </VInput>
          </VCol>
          <VCol cols="12" md="6">
            <VInput v-model="form.end_date" :rules="[required, value => !value || !form.start_date || value >= form.start_date || 'Дата окончания раньше даты начала']">
              <DateMaskInput
                :model-value="form.end_date"
                :min-date="form.start_date || null"
                :disabled="saving"
                @update:model-value="form.end_date = $event || ''"
              >
                <template #label>
                  Дата окончания *
                </template>
              </DateMaskInput>
            </VInput>
          </VCol>
        </VRow>
      </VForm>
      <div v-else-if="caseItem">
        <div class="d-flex flex-wrap align-center gap-2 mb-3">
          <VChip :color="MENTORSHIP_STATUS_COLORS[caseItem.status]" size="small">
            {{ MENTORSHIP_STATUS_LABELS[caseItem.status] }}
          </VChip>
          <span class="text-body-2 text-medium-emphasis">{{ currentRole }} · {{ Math.round(Number(caseItem.progress_percent)) }}% выполнено</span>
        </div>
        <div v-if="tab === 'program'" class="case-summary mb-4">
          <div>
            <div class="text-caption text-medium-emphasis">
              Сотрудник
            </div><strong>{{ caseItem.mentee_name }}</strong><div class="text-caption">
              {{ caseItem.department_name }}
            </div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">
              Наставник
            </div><strong>{{ caseItem.mentor_name || 'Не назначен' }}</strong>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">
              Период
            </div><strong>{{ dateLabel(caseItem.start_date) }} — {{ dateLabel(caseItem.end_date) }}</strong>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">
              Вид и основание
            </div><div>{{ caseItem.assignment_kind === 'statutory' ? 'По законодательству' : 'Адаптация / развитие' }}</div><div class="text-body-2">
              {{ caseItem.basis }}
            </div>
          </div>
        </div>
        <VAlert
          v-if="['archived', 'completed', 'cancelled'].includes(caseItem.status)"
          type="info"
          variant="tonal"
          class="mb-3"
        >
          {{ caseItem.status === 'archived' ? 'Запись в архиве. Результаты и документы доступны для просмотра.' : 'Наставничество закрыто. Оформление документов можно закончить до переноса в архив.' }}
        </VAlert>
        <p v-else-if="tab === 'program'" class="text-body-2 text-medium-emphasis mb-3">
          {{ nextStepText }}
        </p>
        <div v-if="tab === 'program' && caseItem.available_actions.length" class="d-flex flex-wrap gap-2 mb-4">
          <VBtn
            v-if="primaryAction"
            class="general-button"
            :disabled="saving || loading"
            @click="openTransition(primaryAction)"
          >
            {{ actionLabel(primaryAction) }}
          </VBtn>
          <VMenu v-if="secondaryActions.length">
            <template #activator="{ props }">
              <VBtn
                v-bind="props"
                variant="outlined"
                color="secondary"
                append-icon="ri-arrow-down-s-line"
                :disabled="saving || loading"
              >
                Другие действия
              </VBtn>
            </template>
            <VList>
              <VListItem
                v-for="action in secondaryActions"
                :key="action"
                :title="actionLabel(action)"
                @click="openTransition(action)"
              />
            </VList>
          </VMenu>
        </div>
        <VWindow v-model="tab">
          <VWindowItem value="history">
            <p class="text-body-2 text-medium-emphasis mb-4">
              История смены статусов наставничества.
            </p>
            <VProgressLinear v-if="historyLoading" indeterminate />
            <VAlert v-if="historyError" type="error" variant="tonal">
              {{ historyError }} <VBtn variant="text" @click="loadHistory">
                Повторить
              </VBtn>
            </VAlert>
            <VTimeline
              v-if="!historyLoading && !historyError && history.length"
              side="end"
              align="start"
              density="compact"
              truncate-line="both"
              line-inset="8"
              class="mentorship-history w-100"
            >
              <VTimelineItem
                v-for="item in history"
                :key="item.id"
                :dot-color="item.from_status ? 'primary' : 'success'"
                size="x-small"
              >
                <div class="d-flex flex-column ga-2">
                  <div class="d-flex flex-wrap align-center justify-space-between ga-2">
                    <span class="text-body-1 font-weight-medium text-high-emphasis">
                      {{ item.from_status ? `${MENTORSHIP_STATUS_LABELS[item.from_status]} → ` : 'Создание: ' }}{{ MENTORSHIP_STATUS_LABELS[item.to_status] }}
                    </span>
                    <span class="text-caption text-disabled">{{ formatDateTime(item.changed_at) }}</span>
                  </div>
                  <div class="d-flex align-center ga-2 text-body-2 text-medium-emphasis">
                    <VIcon icon="ri-user-line" size="20" />
                    <span>{{ item.changed_by_name }}</span>
                  </div>
                  <CustomDivider v-if="item.reason" />
                  <div v-if="item.reason" class="text-body-2 program-text">
                    {{ item.reason }}
                  </div>
                </div>
              </VTimelineItem>
            </VTimeline>
            <p v-if="!historyLoading && !historyError && !history.length" class="text-medium-emphasis">
              В истории пока нет записей.
            </p>
            <VPagination
              v-if="historyCount > 25"
              v-model="historyPage"
              :length="Math.ceil(historyCount / 25)"
              :disabled="historyLoading"
            />
          </VWindowItem>
          <VWindowItem value="program">
            <VAlert v-if="!program && !loading && !loadError" type="info" variant="tonal">
              Программа не назначена. Координатор может выбрать её через «Изменить», пока запись находится в черновике.
            </VAlert>
            <VExpansionPanels v-else-if="program" multiple variant="accordion">
              <VExpansionPanel v-for="stage in program.stages" :key="stage.id">
                <VExpansionPanelTitle>
                  <span class="program-text">{{ stage.order }}. {{ stage.name }}</span>
                </VExpansionPanelTitle>
                <VExpansionPanelText>
                  <p v-if="stage.description" class="program-text text-body-2 mb-3">
                    {{ stage.description }}
                  </p>
                  <VList>
                    <VListItem
                      v-for="activity in stage.activities"
                      :key="activity.id"
                      :title="activity.name"
                      class="program-activity py-3"
                    >
                      <template #prepend>
                        <VIcon icon="ri-checkbox-circle-line" />
                      </template>
                      <div class="text-body-2 text-medium-emphasis mb-2">
                        Срок: {{ dateLabel(activity.due_date) }} · {{ activityStatusLabels[activity.status] }}
                      </div>
                      <p v-if="activity.description" class="program-text text-body-2 mb-2">
                        {{ activity.description }}
                      </p>
                      <div v-if="activity.completion_criteria" class="text-body-2 mb-2">
                        <strong>Критерий завершения</strong>
                        <p class="program-text mb-0">
                          {{ activity.completion_criteria }}
                        </p>
                      </div>
                      <div v-if="activity.normative_snapshot?.length" class="d-flex flex-wrap gap-1 mt-2">
                        <VChip
                          v-for="reference in activity.normative_snapshot"
                          :key="reference.id"
                          size="x-small"
                          variant="tonal"
                          class="program-reference"
                        >
                          {{ reference.standard }}{{ reference.version ? ` (${reference.version})` : '' }} · {{ reference.number }}
                        </VChip>
                      </div>
                      <div v-if="activity.available_actions?.length" class="d-flex flex-wrap gap-2 mt-3">
                        <VBtn
                          v-for="activityAction in activity.available_actions"
                          :key="activityAction"
                          size="small"
                          variant="outlined"
                          :loading="saving"
                          @click.stop="transitionProgramActivity(activity.id, activityAction)"
                        >
                          {{ activityStatusLabels[activityAction] }}
                        </VBtn>
                      </div>
                    </VListItem>
                  </VList>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
          </VWindowItem>
          <VWindowItem value="diary">
            <VCard v-if="permissions?.can_add_diary" variant="outlined" class="pa-4 mb-4">
              <VSelect
                v-model="diaryActivity"
                :items="activityOptions"
                label="Связанное мероприятие"
                clearable
              />
              <VTextarea v-model="diaryText" label="Новая запись" rows="3" />
              <div class="d-flex justify-end">
                <VBtn
                  class="general-button"
                  :loading="saving"
                  :disabled="!diaryText.trim()"
                  @click="addDiaryEntry"
                >
                  Добавить запись
                </VBtn>
              </div>
            </VCard>
            <VAlert v-if="!diaryEntries.length" type="info" variant="tonal">
              Записей пока нет.
            </VAlert>
            <VCard
              v-for="entry in diaryEntries"
              :key="entry.id"
              variant="outlined"
              class="pa-4 mb-3"
            >
              <div class="d-flex justify-space-between mb-2">
                <strong>{{ entry.author_name }}</strong><span>{{ entry.entry_date }}</span>
              </div>
              <div>{{ entry.text }}</div>
              <div class="d-flex justify-end mt-2">
                <VChip v-if="entry.confirmed_at" color="success" size="small">
                  Подтверждено: {{ entry.confirmed_by_name }}
                </VChip><VBtn
                  v-else-if="permissions?.can_confirm_diary && entry.author !== userStore.user?.id"
                  size="small"
                  variant="outlined"
                  :loading="saving"
                  @click="confirmDiary(entry)"
                >
                  Подтвердить
                </VBtn>
              </div>
            </VCard>
          </VWindowItem>
          <VWindowItem value="competencies">
            <VCard v-if="permissions?.can_assess" variant="outlined" class="pa-4 mb-4">
              <VRow>
                <VCol cols="12" md="5">
                  <VSelect
                    v-model="assessmentForm.competency"
                    :items="competencies"
                    item-title="name"
                    item-value="id"
                    label="Компетенция"
                    @update:model-value="assessmentForm.level = null"
                  />
                </VCol>
                <VCol cols="12" md="3">
                  <VSelect v-model="assessmentForm.assessment_type" :items="Object.entries(assessmentTypeLabels).map(([value, title]) => ({ value, title }))" label="Тип оценки" />
                </VCol>
                <VCol cols="12" md="4">
                  <VSelect v-model="assessmentForm.level" :items="selectedCompetencyLevels" label="Уровень" />
                </VCol>
                <VCol cols="12">
                  <VTextarea v-model="assessmentForm.comment" label="Комментарий" rows="2" />
                </VCol>
              </VRow>
              <div class="d-flex justify-end">
                <VBtn
                  class="general-button"
                  :loading="saving"
                  :disabled="!assessmentForm.competency || assessmentForm.level === null"
                  @click="addAssessment"
                >
                  Сохранить оценку
                </VBtn>
              </div>
            </VCard>
            <VAlert v-if="!assessments.length" type="info" variant="tonal">
              Оценок пока нет.
            </VAlert>
            <VTable v-else>
              <thead><tr><th>Компетенция</th><th>Тип</th><th>Уровень</th><th>Кто оценил</th><th>Дата</th></tr></thead>
              <tbody>
                <tr v-for="assessment in assessments" :key="assessment.id">
                  <td>{{ assessment.competency_name }}</td><td>{{ assessmentTypeLabels[assessment.assessment_type] }}</td><td>{{ assessment.level }}</td><td>{{ assessment.assessed_by_name }}</td><td>{{ new Date(assessment.assessed_at).toLocaleDateString('ru-RU') }}</td>
                </tr>
              </tbody>
            </VTable>
          </VWindowItem>
          <VWindowItem value="tasks-meetings">
            <VRow>
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="pa-4 h-100">
                  <h3 class="mb-3">
                    Связанные задачи
                  </h3><template v-if="permissions?.can_link">
                    <MentorshipLinkPicker
                      v-model="taskLinkForm.task"
                      :case-id="caseItem.id"
                      kind="task"
                      label="Найти задачу по номеру или названию"
                    /><VSelect
                      v-model="taskLinkForm.activity"
                      :items="activityOptions"
                      label="Мероприятие программы"
                      clearable
                    /><VBtn
                      variant="outlined"
                      :disabled="taskLinkForm.task === null"
                      :loading="saving"
                      @click="linkTask"
                    >
                      Связать задачу
                    </VBtn>
                  </template><div v-if="!taskLinks.length" class="text-medium-emphasis mt-3">
                    Задачи ещё не связаны.
                  </div><VList v-if="taskLinks.length" class="mt-3">
                    <VListItem
                      v-for="link in taskLinks"
                      :key="link.id"
                      :title="`Задача №${link.task_number}`"
                      :subtitle="(apiStore.responsibilityEntriesStatuses as Record<string, string>)[link.task_status] || link.task_status"
                    >
                      <template #append>
                        <VBtn
                          variant="text"
                          size="small"
                          :loading="openingTask === link.id"
                          @click="openTask(link)"
                        >
                          Открыть задачу
                        </VBtn>
                        <VBtn
                          v-if="permissions?.can_link"
                          icon="ri-link-unlink-m"
                          variant="text"
                          size="small"
                          aria-label="Убрать связь с задачей"
                          @click="unlinkTarget = { id: link.id, kind: 'task' }"
                        />
                      </template>
                    </VListItem>
                  </VList>
                </VCard>
              </VCol>
              <VCol cols="12" md="6">
                <VCard variant="outlined" class="pa-4 h-100">
                  <h3 class="mb-3">
                    Встречи
                  </h3><template v-if="permissions?.can_link">
                    <MentorshipLinkPicker
                      v-model="meetingForm.calendar_event"
                      :case-id="caseItem.id"
                      kind="meeting"
                      label="Найти встречу по названию"
                    /><VSelect
                      v-model="meetingForm.activity"
                      :items="activityOptions"
                      label="Мероприятие программы"
                      clearable
                    /><VTextarea v-model="meetingForm.protocol" label="Протокол встречи" rows="2" /><VBtn
                      variant="outlined"
                      :disabled="meetingForm.calendar_event === null"
                      :loading="saving"
                      @click="linkMeeting"
                    >
                      Связать встречу
                    </VBtn>
                  </template><div v-if="!meetings.length" class="text-medium-emphasis mt-3">
                    Встречи ещё не связаны.
                  </div><VList v-if="meetings.length" class="mt-3">
                    <VListItem
                      v-for="meeting in meetings"
                      :key="meeting.id"
                      :title="meeting.event_title"
                      :subtitle="`${new Date(meeting.event_start).toLocaleString('ru-RU')} · ${meeting.protocol || 'Протокол не заполнен'}`"
                    >
                      <template v-if="permissions?.can_link" #append>
                        <VBtn
                          icon="ri-edit-line"
                          variant="text"
                          size="small"
                          aria-label="Изменить протокол"
                          @click="protocolEdit = meeting; protocolText = meeting.protocol; protocolReason = ''"
                        /><VBtn
                          icon="ri-link-unlink-m"
                          variant="text"
                          size="small"
                          aria-label="Убрать связь со встречей"
                          @click="unlinkTarget = { id: meeting.id, kind: 'meeting' }"
                        />
                      </template>
                    </VListItem>
                  </VList>
                </VCard>
              </VCol>
            </VRow>
          </VWindowItem>
          <VWindowItem value="knowledge">
            <VAlert
              v-if="documentDownload"
              type="success"
              variant="tonal"
              class="mb-3"
            >
              <a :href="documentDownload.url" :download="documentDownload.filename">Скачать повторно: {{ documentDownload.filename }}</a>
            </VAlert>
            <VAlert v-if="!materials.length" type="info" variant="tonal">
              Материалы пока не опубликованы.
            </VAlert>
            <VCard
              v-for="material in materials"
              :key="material.id"
              variant="outlined"
              class="pa-4 mb-3"
            >
              <strong>{{ material.title }}</strong>
              <div v-for="version in material.versions.filter(item => item.published_at)" :key="version.id" class="d-flex align-center justify-space-between mt-3">
                <div>
                  <span>Версия {{ version.version }}</span><div v-if="version.text" class="text-medium-emphasis">
                    {{ version.text }}
                  </div><a
                    v-if="version.external_url"
                    :href="version.external_url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >Открыть материал</a><VBtn
                    v-if="version.file"
                    variant="text"
                    size="small"
                    :loading="saving"
                    @click="downloadMaterial(version)"
                  >
                    Скачать {{ version.file_name || 'файл' }}
                  </VBtn>
                </div>
                <VChip v-if="acknowledgements.some(item => item.version === version.id && item.employee === userStore.user?.id)" color="success" size="small">
                  Ознакомление сохранено
                </VChip><VBtn
                  v-else-if="permissions?.can_acknowledge"
                  size="small"
                  variant="outlined"
                  :loading="saving"
                  @click="acknowledgeMaterial(version.id)"
                >
                  Ознакомлен
                </VBtn>
              </div>
            </VCard>
          </VWindowItem>
          <VWindowItem value="tests">
            <VList v-if="testAttempts.length" class="mb-3">
              <VListItem
                v-for="attempt in testAttempts"
                :key="attempt.id"
                :title="`${attempt.employee === userStore.user?.id ? 'Моя попытка' : 'Попытка участника'} от ${new Date(attempt.started_at).toLocaleString('ru-RU')}`"
                :subtitle="({ active: 'В процессе', review: 'На проверке', completed: 'Завершена', expired: 'Время истекло' })[attempt.status]"
                @click="selectAttempt(attempt)"
              />
            </VList>
            <VAlert v-if="!publishedTestVersions.length" type="info" variant="tonal">
              Опубликованных тестов пока нет.
            </VAlert><VAlert v-else-if="!permissions?.can_test && !testAttempt" type="info" variant="tonal">
              Прохождение тестов доступно участникам действующего наставничества.
            </VAlert>
            <div v-if="!testAttempt && permissions?.can_test && publishedTestVersions.length" class="d-flex flex-wrap align-center gap-3">
              <VSelect
                v-model="selectedTestVersion"
                :items="publishedTestVersions"
                label="Выберите тест"
                class="flex-grow-1"
              /><VBtn
                class="general-button"
                :disabled="!selectedTestVersion"
                :loading="saving"
                @click="startTest"
              >
                Начать
              </VBtn>
            </div>
            <VCard v-if="testAttempt" variant="outlined" class="pa-4">
              <VBtn
                v-if="testAttempt.status !== 'active'"
                variant="text"
                class="mb-3"
                @click="testAttempt = null"
              >
                К выбору теста
              </VBtn>
              <VAlert
                v-if="testAttempt.status !== 'active'"
                :type="testAttempt.passed ? 'success' : 'info'"
                variant="tonal"
                class="mb-4"
              >
                {{ testAttempt.status === 'expired' ? 'Время прохождения истекло.' : testAttempt.status === 'review' ? 'Ответы ожидают ручной проверки.' : `Результат: ${testAttempt.score_percent ?? 0}%` }}
              </VAlert>
              <div v-for="(question, index) in testAttempt.questions" :key="question.id" class="mb-5">
                <strong>{{ index + 1 }}. {{ question.text }}</strong><VTextarea
                  v-if="question.kind === 'text'"
                  v-model="testAnswers[question.id]"
                  :disabled="testAttempt.status !== 'active' || saving || testAttempt.employee !== userStore.user?.id || !permissions?.can_test"
                  class="mt-2"
                  rows="2"
                /><VSelect
                  v-else
                  v-model="testAnswers[question.id]"
                  :disabled="testAttempt.status !== 'active' || saving || testAttempt.employee !== userStore.user?.id || !permissions?.can_test"
                  :multiple="question.kind === 'multiple'"
                  :items="question.options"
                  :item-title="item => typeof item === 'string' ? item : item.label"
                  :item-value="item => typeof item === 'string' ? item : item.value"
                  class="mt-2"
                />
              </div>
              <div v-if="testAttempt.status === 'active' && testAttempt.employee === userStore.user?.id && permissions?.can_test" class="d-flex justify-end">
                <VBtn class="general-button" :loading="saving" @click="submitTest">
                  Завершить тест
                </VBtn>
              </div>
            </VCard>
          </VWindowItem>
          <VWindowItem value="documents">
            <VAlert
              v-if="documentDownload"
              type="success"
              variant="tonal"
              class="mb-3"
            >
              <a :href="documentDownload.url" :download="documentDownload.filename">Скачать повторно: {{ documentDownload.filename }}</a>
            </VAlert>
            <div v-if="documents.length" class="text-h6 mb-2">
              Подтверждённые и загруженные документы
            </div>
            <VList v-if="documents.length">
              <VListItem
                v-for="document in documents"
                :key="document.id"
                :title="`${document.file_name || 'Документ'} · версия ${document.version}`"
                :subtitle="`${documentTemplates.find(item => item.id === document.template)?.name || 'Без вида документа'} · ${document.confirmed_at ? 'Подтверждён' : 'Ожидает подтверждения'}`"
              >
                <template #append>
                  <VBtn
                    variant="text"
                    size="small"
                    :loading="saving"
                    @click="downloadUploadedDocument(document)"
                  >
                    Скачать
                  </VBtn>
                  <VBtn
                    v-if="!document.confirmed_at && permissions?.can_confirm_documents"
                    size="small"
                    variant="outlined"
                    :loading="saving"
                    @click="confirmDocument(document)"
                  >
                    Подтвердить
                  </VBtn>
                </template>
              </VListItem>
            </VList>
            <p v-else class="text-body-2 text-medium-emphasis mb-4" role="status">
              Оформленные документы пока не загружены.
            </p>
            <VExpansionPanels v-if="permissions?.can_upload_documents" variant="accordion" class="my-4">
              <VExpansionPanel title="Загрузить оформленный документ">
                <VExpansionPanelText>
                  <VSelect
                    v-model="uploadTemplate"
                    :items="documentTemplates.filter(item => item.is_active).map(item => ({ value: item.id, title: `${item.name}${requiredDocumentTemplates.some(required => required.id === item.id) ? ' (для завершения)' : ''}` }))"
                    label="Вид документа"
                    clearable
                    hint="Выберите вид документа, чтобы он учитывался в комплекте наставничества."
                    persistent-hint
                    class="mb-3"
                  /><VFileInput
                    v-model="uploadFile"
                    label="Файл документа"
                    accept=".pdf,.docx,.jpg,.jpeg,.png"
                    hint="PDF, DOCX или скан JPG/PNG, до 20 МБ."
                    persistent-hint
                    show-size
                    :disabled="saving"
                  /><VBtn
                    :disabled="!uploadFile"
                    :loading="saving"
                    variant="outlined"
                    @click="uploadDocument"
                  >
                    Загрузить
                  </VBtn>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
            <VAlert
              v-if="missingDocuments.length && !loading && !loadError && !['completed', 'cancelled', 'archived'].includes(caseItem.status)"
              type="info"
              variant="tonal"
              class="mb-4"
            >
              Для завершения по правилам организации нужны: {{ missingDocuments.map(item => item.name).join(', ') }}. Файлы должны быть загружены и подтверждены наставником или координатором.
            </VAlert>
            <VExpansionPanels variant="accordion" class="mt-4">
              <VExpansionPanel title="Подготовить документ">
                <VExpansionPanelText>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    Бланки заполняются по данным карточки. Проверьте сведения и условия оплаты, оформите подписи в принятом в организации порядке. Скачивание файла не подтверждает подписание.
                  </p>
                  <div v-for="stage in ['start', 'middle', 'finish']" :key="stage" class="mb-4">
                    <h3 class="text-subtitle-1 font-weight-medium mb-2">
                      {{ documentStageLabels[stage].title }}
                    </h3>
                    <VList lines="two">
                      <VListItem
                        v-for="item in generatedDocuments.filter(document => document.stage === stage)"
                        :key="item.code"
                        :title="item.title"
                        :subtitle="item.available ? item.description : 'Станет доступен на соответствующем этапе.'"
                      >
                        <template #append>
                          <div class="d-flex gap-2">
                            <VBtn
                              size="small"
                              variant="text"
                              :disabled="!item.available"
                              :loading="saving"
                              :aria-label="`Скачать PDF: ${item.title}`"
                              @click="downloadDocument(item, 'pdf')"
                            >
                              PDF
                            </VBtn>
                            <VBtn
                              size="small"
                              variant="text"
                              :disabled="!item.available"
                              :loading="saving"
                              :aria-label="`Скачать Word: ${item.title}`"
                              @click="downloadDocument(item, 'docx')"
                            >
                              Word
                            </VBtn>
                          </div>
                        </template>
                      </VListItem>
                    </VList>
                  </div>
                </VExpansionPanelText>
              </VExpansionPanel>
            </VExpansionPanels>
          </VWindowItem>
          <VWindowItem value="feedback">
            <VCard v-if="permissions?.can_feedback" variant="outlined" class="pa-4 mb-4">
              <VRating v-model="feedbackForm.score" color="warning" /><VTextarea v-model="feedbackForm.text" label="Комментарий или запрос помощи" rows="3" /><VCheckbox v-model="feedbackForm.is_confidential" label="Конфиденциальное обращение" /><div class="d-flex justify-end">
                <VBtn class="general-button" :loading="saving" @click="addFeedback">
                  Отправить
                </VBtn>
              </div>
            </VCard>
            <VAlert
              v-for="risk in risks.filter(item => item.status === 'open')"
              :key="risk.id"
              type="warning"
              variant="tonal"
              class="mb-2"
            >
              {{ risk.title }}
            </VAlert>
            <VList v-if="feedback.length">
              <VListItem
                v-for="item in feedback"
                :key="item.id"
                :title="`Оценка: ${item.score} из 5`"
                :subtitle="item.text || 'Без комментария'"
              />
            </VList><VAlert v-else type="info" variant="tonal">
              Обратной связи пока нет. Её оставляют участники наставничества.
            </VAlert>
          </VWindowItem>
        </VWindow>
      </div>
    </VCardText>
    <VCardActions v-if="isCreate || editing">
      <VBtn
        variant="outlined"
        color="secondary"
        :disabled="saving"
        @click="editing ? cancelEdit() : caseDialogRef?.close()"
      >
        Отмена
      </VBtn>
      <VBtn
        class="general-button"
        variant="elevated"
        :loading="saving"
        :disabled="loading || (!capabilities?.can_create && isCreate)"
        @click="save"
      >
        {{ editing ? 'Сохранить' : 'Создать черновик' }}
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    ref="transitionDialogRef"
    :model-value="!!transitionTarget"
    :title="actionLabel(transitionTarget) || 'Изменить статус'"
    max-width="520"
    :busy="saving"
    :draft="transitionReason"
    @update:model-value="!$event && (transitionTarget = '')"
  >
    <VCardText>
      <p class="mb-3">
        Изменение статуса сохраняется в истории наставничества.
      </p>
      <p v-if="transitionTarget === 'cancelled' && caseItem && ['active', 'paused', 'final_review'].includes(caseItem.status)" class="text-body-2 mb-3">
        При досрочном отказе наставника или отмене поручения работодателем учтите срок предупреждения — не менее трёх рабочих дней по статье 351.8 ТК РФ. Изменение статуса не заменяет кадровые документы.
      </p>
      <VAlert
        v-if="transitionTarget === 'completed' && caseItem?.status !== 'archived' && missingDocuments.length"
        type="info"
        variant="tonal"
        class="mb-3"
      >
        Для завершения по правилам организации нужны: {{ missingDocuments.map(item => item.name).join(', ') }}.
        <VBtn variant="text" @click="transitionTarget = ''; tab = 'documents'">
          К документам
        </VBtn>
      </VAlert>
      <VTextarea v-model="transitionReason" label="Комментарий / основание изменения" rows="3" /><VAlert v-if="error" type="error" variant="tonal">
        {{ error }}
      </VAlert>
    </VCardText><VCardActions>
      <VSpacer /><VBtn :disabled="saving" @click="transitionDialogRef?.close()">
        Отмена
      </VBtn><VBtn :loading="saving" @click="transition(transitionTarget)">
        Подтвердить
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    ref="protocolDialogRef"
    :model-value="!!protocolEdit"
    title="Протокол встречи"
    max-width="620"
    :busy="saving"
    :draft="[protocolText, protocolReason]"
    @update:model-value="!$event && (protocolEdit = null)"
  >
    <VCardText>
      <VTextarea v-model="protocolText" label="Протокол" rows="5" /><VTextField v-model="protocolReason" label="Причина изменения" /><VAlert v-if="error" type="error" variant="tonal">
        {{ error }}
      </VAlert>
    </VCardText><VCardActions>
      <VSpacer /><VBtn :disabled="saving" @click="protocolDialogRef?.close()">
        Отмена
      </VBtn><VBtn :loading="saving" @click="saveProtocol">
        Сохранить
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <MentorshipDialog
    :model-value="!!unlinkTarget"
    title="Убрать связь?"
    max-width="460"
    :busy="saving"
    @update:model-value="!$event && (unlinkTarget = null)"
  >
    <VCardText>
      Задача или событие останется в исходном разделе.<VAlert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-3"
      >
        {{ error }}
      </VAlert>
    </VCardText><VCardActions>
      <VSpacer /><VBtn :disabled="saving" @click="unlinkTarget = null">
        Отмена
      </VBtn><VBtn :loading="saving" @click="unlink">
        Убрать связь
      </VBtn>
    </VCardActions>
  </MentorshipDialog>
  <EmployeeSelectionModal
    ref="menteeModal"
    :employees="apiStore.employees"
    title="Выберите наставляемого"
    @select="selectMentee"
  />
  <EmployeeSelectionModal
    ref="mentorModal"
    :employees="apiStore.employees"
    title="Выберите наставника"
    @select="selectMentor"
  />
</template>

<style scoped>
.mentorship-case-body { min-inline-size: 0; }
.mentorship-history :deep(.v-timeline-item__body) { min-inline-size: 0; inline-size: 100%; }
.mentorship-case-card { border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); }
.case-summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px 24px; padding: 16px; background: rgba(var(--v-theme-on-surface), .03); border-radius: 6px; overflow-wrap: anywhere; }
.case-tabs { max-inline-size: 100%; flex: none; }
.case-tabs :deep(.v-tab) { flex-shrink: 0; }
.program-text { min-inline-size: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
.program-activity { align-items: start; }
.program-activity :deep(.v-list-item__content) { min-inline-size: 0; }
.program-activity :deep(.v-list-item-title) { white-space: normal; overflow-wrap: anywhere; }
.program-reference { max-inline-size: 100%; block-size: auto; min-block-size: 20px; padding-block: 3px; }
.program-reference :deep(.v-chip__content) { white-space: normal; overflow-wrap: anywhere; }
.mentorship-case-body :deep(.v-list-item__subtitle) { white-space: normal; -webkit-line-clamp: unset; }
.mentorship-case-body :deep(.v-list-item__title) { white-space: normal; }
.mentorship-case-body :deep(.v-input > .v-input__control) { min-inline-size: 0; inline-size: 100%; }
.mentorship-case-body :deep(.v-input > .v-input__control > .v-input) { inline-size: 100%; }
@media (max-width: 1000px) {
  .case-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 700px) {
  .case-summary { grid-template-columns: 1fr; }
  .mentorship-case-card > .v-card-text { padding: 16px !important; }
  .mentorship-case-body :deep(.v-list-item) { grid-template-areas: "prepend content" "append append"; grid-template-columns: auto 1fr; }
  .mentorship-case-body :deep(.v-list-item__append) { flex-wrap: wrap; gap: 8px; margin-block-start: 8px; }
  .mentorship-case-body :deep(.v-expansion-panel-text__wrapper) { padding-inline: 8px; }
}
</style>
